import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Button, Dialog, Typography } from '@zendesk/forethought-elements';
import { useDeleteIntentMutation } from 'src/services/workflow-builder-canvas/workflowBuilderCanvasApi';
import { setGlobalToastOptions } from 'src/slices/ui/uiSlice';
import { useAppDispatch } from 'src/store/hooks';

interface DeleteWorkflowDialogProps {
  intentIdToDelete: string;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export const DeleteWorkflowDialog = ({
  intentIdToDelete,
  isOpen,
  onClose,
  onSuccess,
}: DeleteWorkflowDialogProps) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation('workflow-builder');
  const [isDeleting, setIsDeleting] = useState(false);
  const [mutate] = useDeleteIntentMutation({
    fixedCacheKey: 'deleteIntent',
  });

  return (
    <Dialog
      footer={
        <>
          <Button onClick={onClose} variant='ghost'>
            {t('deleteWorkflowDialog.cancel')}
          </Button>
          <Button
            aria-label={t('deleteWorkflowDialog.confirmAriaLabel')}
            disabled={isDeleting}
            isLoading={isDeleting}
            onClick={async () => {
              setIsDeleting(true);
              try {
                const res = await mutate({
                  intentDefinitionId: intentIdToDelete,
                });

                if ('data' in res && res.data?.success) {
                  onSuccess?.();
                  dispatch(
                    setGlobalToastOptions({
                      autoHideDuration: 3000,
                      title: t('deleteWorkflowDialog.successToast'),
                      variant: 'main',
                    }),
                  );
                }
              } catch (error) {
                dispatch(
                  setGlobalToastOptions({
                    autoHideDuration: 3000,
                    subtitle: t('deleteWorkflowDialog.errorToastSubtitle'),
                    title: t('deleteWorkflowDialog.errorToastTitle'),
                    variant: 'danger',
                  }),
                );
              }

              setIsDeleting(false);
              onClose();
            }}
            variant='secondary'
          >
            {t('deleteWorkflowDialog.delete')}
          </Button>
        </>
      }
      hideCloseButton
      onClose={onClose}
      open={isOpen}
      title={t('deleteWorkflowDialog.title')}
    >
      <Typography variant='font14'>
        <Trans
          components={{ break: <br /> }}
          i18nKey='deleteWorkflowDialog.body'
          ns='workflow-builder'
        />
      </Typography>
    </Dialog>
  );
};
